import { useEffect, useMemo, useState } from "preact/hooks";
import { fetchClient, parseUrl } from "../api";
import type { ClientData, DashboardConversationRow } from "../types";
import { fmtCanonicalPt, fmtDateTime, fmtDisplayName, fmtPhoneDisplay } from "../format";
import { AlertIconBadge, Badge, PriorityBadge, StateBadge, StateIconBadge } from "../components/atoms/Badge";
import { AppShell } from "../components/AppShell";
import { crmFinalLabel, EgoLink, getCrmFinalTarget } from "../components/CrmLinks";
import { Pagination, usePagination } from "../components/Pagination";

interface ClientFilters {
  intent: string;
  classification: string;
  dateFrom: string;
  dateTo: string;
}

function pass(row: DashboardConversationRow, filters: ClientFilters): boolean {
  if (filters.intent && row.intent !== filters.intent) return false;
  if (filters.classification && row.classification !== filters.classification) return false;
  const d = new Date(row.updated_at || row.created_at || 0).getTime();
  if (filters.dateFrom && d < new Date(`${filters.dateFrom}T00:00:00-03:00`).getTime()) return false;
  if (filters.dateTo && d > new Date(`${filters.dateTo}T23:59:59-03:00`).getTime()) return false;
  return true;
}

function suffix(mock: boolean): string {
  return mock ? "?mock=1" : "";
}

export function ClientPage({ mock }: { mock: boolean }) {
  const [data, setData] = useState<ClientData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filters, setFilters] = useState<ClientFilters>({
    intent: "",
    classification: "",
    dateFrom: "",
    dateTo: "",
  });
  const pageSize = 20;
  const filterKey = JSON.stringify(filters);

  useEffect(() => {
    setPage(1);
  }, [filterKey]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchClient({ ...parseUrl(), mock }, mock ? {} : {
      intent: filters.intent,
      classification: filters.classification,
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
      page,
      page_size: pageSize,
    })
      .then((d) => {
        if (!d || d.ok === false) setError(d?.error || "Cliente não encontrado.");
        else {
          setData(d);
          setSelectedId(d.conversations[0]?.id || null);
        }
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, [mock, filterKey, page]);

  const rows = useMemo(
    () => {
      const sourceRows = data?.conversations || [];
      return mock ? sourceRows.filter((row) => pass(row, filters)) : sourceRows;
    },
    [data, filterKey, mock],
  );
  const localPagination = usePagination(rows, {
    pageSize,
    resetKey: filterKey,
  });
  const pageItems = mock ? localPagination.pageItems : rows;
  const serverTotal = data?.pagination?.total_items ?? rows.length;
  const serverPageCount = Math.max(1, data?.pagination?.page_count ?? 1);
  const serverRangeStart = serverTotal ? (page - 1) * pageSize + 1 : 0;
  const serverRangeEnd = Math.min(serverTotal, (page - 1) * pageSize + rows.length);
  const selected = pageItems.find((row) => row.id === selectedId)
    || pageItems[0]
    || null;

  if (loading) {
    return (
      <AppShell active="clientes" title="Cliente" mock={mock}>
        <div class="state-msg">Carregando cliente…</div>
      </AppShell>
    );
  }
  if (error || !data) {
    return (
      <AppShell active="clientes" title="Cliente" mock={mock}>
        <div class="state-error">{error}</div>
      </AppShell>
    );
  }

  const filtersHeader = (
    <section class="filter-bar filter-bar--header filter-bar--compact">
      <label class="field">
        <span>Intent</span>
        <select
          value={filters.intent}
          onChange={(e) => setFilters({ ...filters, intent: e.currentTarget.value })}
        >
          <option value="">Todos</option>
          {data.filters.intents.map((intent) => (
            <option value={intent} key={intent}>{fmtCanonicalPt(intent)}</option>
          ))}
        </select>
      </label>
      <label class="field">
        <span>Classificação</span>
        <select
          value={filters.classification}
          onChange={(e) => setFilters({ ...filters, classification: e.currentTarget.value })}
        >
          <option value="">Todas</option>
          {data.filters.classifications.map((c) => <option value={c} key={c}>{c}</option>)}
        </select>
      </label>
      <label class="field">
        <span>De</span>
        <input
          type="date"
          value={filters.dateFrom}
          onInput={(e) => setFilters({ ...filters, dateFrom: e.currentTarget.value })}
        />
      </label>
      <label class="field">
        <span>Até</span>
        <input
          type="date"
          value={filters.dateTo}
          onInput={(e) => setFilters({ ...filters, dateTo: e.currentTarget.value })}
        />
      </label>
    </section>
  );
  const clientInfoHeader = (
    <section class="crm-strip crm-strip--header">
      <div class="crm-strip-item crm-strip-item--compact">
        <span>Entity eGO</span>
        <strong>
          <EgoLink
            id={data.client.ego_entity_id}
            label="Entity"
            kind="person"
          />
        </strong>
      </div>
      <div class="crm-strip-item crm-strip-item--compact">
        <span>CRM final eGO</span>
        <strong>{data.crm_emissions?.length || 0}</strong>
      </div>
    </section>
  );
  const globalData = (
    <section class="metric-grid metric-grid--singleline">
      {data.metrics.map((m) => (
        <div class="metric-card" key={m.key}>
          <div class="metric-value">{m.value}</div>
          <div class="metric-label">{m.label}</div>
        </div>
      ))}
    </section>
  );

  return (
    <AppShell
      active="clientes"
      title={fmtDisplayName(data.client.name)}
      subtitle={`${fmtPhoneDisplay(data.client.phone)} · ${data.client.email || ""}`}
      mock={mock}
      headerInfo={clientInfoHeader}
      headerExtra={filtersHeader}
      bodyTop={globalData}
      inspector={<ClientConversationInspector row={selected} mock={mock} />}
    >
      <section class="rows-panel">
        <div class="rows-panel-head">
          <span>{rows.length} conversas e oportunidades</span>
          {mock && <Badge variant="info">mock</Badge>}
        </div>
        <div class="rows-panel-scroll">
          {pageItems.map((row) => (
            <ClientConversationRow
              key={row.id}
              row={row}
              mock={mock}
              selected={selected?.id === row.id}
              onSelect={() => setSelectedId(row.id)}
            />
          ))}
        </div>
        <Pagination
          page={mock ? localPagination.page : page}
          pageCount={mock ? localPagination.pageCount : serverPageCount}
          rangeStart={mock ? localPagination.rangeStart : serverRangeStart}
          rangeEnd={mock ? localPagination.rangeEnd : serverRangeEnd}
          totalItems={mock ? localPagination.totalItems : serverTotal}
          onPageChange={mock ? localPagination.setPage : setPage}
        />
      </section>
    </AppShell>
  );
}

function ClientConversationRow(
  { row, mock, selected, onSelect }: {
    row: DashboardConversationRow;
    mock: boolean;
    selected: boolean;
    onSelect: () => void;
  },
) {
  const selectFromRow = (event: Event) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest("a, button")) return;
    onSelect();
  };
  const selectFromKeyboard = (event: KeyboardEvent) => {
    if (event.currentTarget !== event.target) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onSelect();
  };

  return (
    <article
      class={`conv-row conv-row--selectable ${selected ? "is-selected" : ""}`}
      role="button"
      tabIndex={0}
      onClick={selectFromRow}
      onKeyDown={selectFromKeyboard}
      aria-pressed={selected ? "true" : "false"}
    >
      <div class="conv-row-main">
        <div class="conv-row-title">
          <button class="link-button" type="button" onClick={onSelect}>
            {fmtCanonicalPt(row.intent || "") || "Conversa"}
          </button>
          <span>{fmtDateTime(row.updated_at)}</span>
        </div>
        <div class="conv-row-meta">
          <span>{row.msg_count || 0} msgs</span>
          <span>{row.match_count || 0} matches</span>
          <span>{row.close_reason || "aberta"}</span>
          {row.lead_sent_at && <span>Lead enviado {fmtDateTime(row.lead_sent_at)}</span>}
        </div>
      </div>
      <div class="conv-row-badges">
        <PriorityBadge priority={row.classification as never} />
        <StateIconBadge state={row.terminal_state} closeReason={row.close_reason} />
        <AlertIconBadge count={row.alert_count} alerts={row.alerts} />
      </div>
      <div class="conv-row-actions">
        {(() => {
          const crmFinal = getCrmFinalTarget(
            row.crm_final_emission_kind,
            row.ego_opportunity_id,
            row.ego_lead_id,
            row.ego_opportunity_url,
            row.ego_lead_url,
          );
          return crmFinal.url
            ? (
              <a class="btn" href={crmFinal.url} target="_blank" rel="noopener">
                {crmFinalLabel(row.crm_final_emission_kind, true)}
              </a>
            )
            : null;
        })()}
        <a class="btn" href={`/conv/${row.id}${suffix(mock)}`}>Abrir</a>
      </div>
    </article>
  );
}

function ClientConversationInspector(
  { row, mock }: { row?: DashboardConversationRow | null; mock: boolean },
) {
  const pageSuffix = suffix(mock);
  if (!row) {
    return <div class="inspector-card"><div class="state-msg">Sem conversa selecionada.</div></div>;
  }
  const events = [
    ["Criada", row.created_at],
    ["Atualizada", row.updated_at],
    ["Lead enviado", row.lead_sent_at],
  ].filter(([, value]) => !!value);
  const crmFinal = getCrmFinalTarget(
    row.crm_final_emission_kind,
    row.ego_opportunity_id,
    row.ego_lead_id,
    row.ego_opportunity_url,
    row.ego_lead_url,
  );

  return (
    <div class="inspector-stack">
      <section class="inspector-card">
        <h2>Resumo</h2>
        <div class="inspector-title">{fmtCanonicalPt(row.intent || "") || "Conversa"}</div>
        <div class="inspector-muted">{fmtDateTime(row.updated_at)}</div>
        <div class="inspector-badges">
          <PriorityBadge priority={row.classification as never} />
          <StateBadge state={row.terminal_state} />
        </div>
        <dl class="inspector-kv">
          <dt>Motivo</dt>
          <dd>{row.close_reason || "aberta"}</dd>
          <dt>Tipo CRM final</dt>
          <dd>{row.crm_final_emission_kind ? crmFinalLabel(row.crm_final_emission_kind, true) : "—"}</dd>
          <dt>{crmFinalLabel(row.crm_final_emission_kind)}</dt>
          <dd>
            <EgoLink
              id={crmFinal.id}
              label={crmFinal.label}
              kind="lead"
              url={crmFinal.url}
            />
          </dd>
          <dt>Mensagens</dt>
          <dd>{row.msg_count || 0}</dd>
          <dt>Matches</dt>
          <dd>{row.match_count || 0}</dd>
        </dl>
        <a class="btn inspector-button" href={`/conv/${row.id}${pageSuffix}`}>Abrir conversa</a>
      </section>
      <section class="inspector-card">
        <h2>Alertas</h2>
        {row.alerts?.length
          ? row.alerts.map((alert) => (
            <div class="inspector-alert" key={`${alert.label}-${alert.detail}`}>
              <Badge variant={alert.level === "danger" ? "danger" : alert.level === "warn" ? "warn" : "info"}>
                {alert.label}
              </Badge>
              {alert.detail && <span>{alert.detail}</span>}
            </div>
          ))
          : <div class="inspector-muted">Sem alertas.</div>}
      </section>
      <section class="inspector-card">
        <h2>Últimos eventos</h2>
        <div class="event-list">
          {events.map(([label, value]) => (
            <div class="event-row" key={label}>
              <span>{label}</span>
              <strong>{fmtDateTime(value)}</strong>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
